/**
 * Applies the resolved sales person to an enquiry that has none. Renders nothing.
 *
 * `sales_person.get_salesperson` answers who handles a given customer + company, and the picker
 * has been showing that answer at the top of its dropdown marked "handles this customer" — leaving
 * the user to click and agree with something the server had already decided. On every enquiry.
 *
 * ---- Since 2026-09-14: it also FOLLOWS a customer or company change ----
 *
 * The sales person belongs to the customer (Akhil, 5a). When either half of the pair is changed
 * on the header card, the person on the enquiry handled the previous customer, so the lookup's
 * answer for the new one replaces them. The pair the screen OPENED with is remembered per
 * enquiry; only a change from that counts. See `lib/salesPersonDefault.ts` for the decision.
 *
 * ---- What it must not do ----
 *
 *   - overwrite a sales person already on the enquiry when nothing changed. Somebody chose that,
 *     or a previous run of this did, and either outranks a fresh lookup on a plain page open.
 *   - fire when the lookup resolved more than one person. That is a genuine choice and stays one.
 *   - retry after a failed write, or write twice for one enquiry — the same fire-once discipline
 *     `AutoMatcher` and `ContractApplier` use, for the same reason: this is an unrequested write
 *     to a real document, and a loop of them is far worse than not writing at all.
 *
 * A component rather than a hook because the writer it needs lives after `RfqPage`'s early
 * returns; same latest-ref pattern as its two siblings.
 */

import { useEffect, useRef } from 'react'
import { useRecommendedSalesPerson } from '../../lib/api/useMasters'
import { decideSalesPerson } from '../../lib/salesPersonDefault'

export function SalesPersonDefaulter({
  enquiryId,
  customer,
  company,
  current,
  enabled,
  apply,
  onKept,
}: {
  /** Keys the fire-once guard — moving to another enquiry must be allowed to write again. */
  enquiryId: string
  customer: string
  company: string
  /** The enquiry's existing sales person. Anything non-empty stops this entirely. */
  current: string
  /** False while another bulk action owns the document. */
  enabled: boolean
  apply: (salesPerson: string) => Promise<void>
  /** The customer changed, nobody is assigned to the new one, and `salesPerson` stayed. */
  onKept?: (salesPerson: string) => void
}) {
  const { recommended, resolvedNobody } = useRecommendedSalesPerson(customer, company)
  const pair = `${customer}\u0000${company}`
  // What the screen opened with, per enquiry — a change FROM this is what re-applies.
  const opened = useRef<{ enquiryId: string; pair: string } | null>(null)
  if (opened.current === null || opened.current.enquiryId !== enquiryId) {
    opened.current = { enquiryId, pair }
  }
  // Fire once per (enquiry, pair): a second change is a new key and may write again.
  const applied = useRef<string | null>(null)
  const noted = useRef<string | null>(null)
  const running = useRef(false)
  const latest = useRef({ apply, onKept })
  useEffect(() => {
    latest.current = { apply, onKept }
  })

  useEffect(() => {
    if (!enabled || running.current) return
    const key = `${enquiryId}\u0000${pair}`
    if (applied.current === key) return
    const pairChanged = opened.current?.pair !== pair
    const next = decideSalesPerson({ current, recommended, pairChanged })
    if (next === null) {
      // Nobody handles the NEW customer and the field was not blanked (2026-09-18) — said once,
      // so the purchaser knows the name on the enquiry came from the previous customer.
      if (pairChanged && resolvedNobody && current.trim() !== '' && noted.current !== key) {
        noted.current = key
        latest.current.onKept?.(current)
      }
      return
    }

    applied.current = key
    running.current = true
    void (async () => {
      try {
        await latest.current.apply(next)
      } catch {
        // Let a later render try again — otherwise a transient failure leaves the field blank for
        // the rest of the session with nothing saying why. The mutation reports the error itself.
        applied.current = null
      } finally {
        running.current = false
      }
    })()
  }, [enquiryId, pair, current, recommended, resolvedNobody, enabled])

  return null
}
